import { fetchQuery, graphql } from 'react-relay';
import {
	AUTHORIZATION_FAILED_INSUFFICIENT_PERMISSIONS,
	DATA_CLASSIFICATION_NO_ACCESS,
	FAILED_TO_FETCH,
	getErrorType,
} from '@jira/forge__forge-ui-analytics/src/common/utils/get-error-type/index.tsx';
import { measureExecutionTimeMetrics } from '@jira/forge__forge-ui-analytics/src/common/utils/measure-execution-time/index.tsx';
import { FORGE_SAMPLING_RATE } from '@jira/forge__forge-ui-analytics/src/constants.tsx';
import {
	fireInitializationFetchFailedEvent,
	fireInitializationFetchFinishedEvent,
} from '@jira/forge__forge-ui-analytics/src/services/fetch-modules/index.tsx';
import { SOURCE_UNKNOWN } from '@jira/forge__forge-ui-constants/src/constants.tsx';
import type { Source } from '@jira/forge__forge-ui-types/src/common/types/analytics.tsx';
import type {
	AggExtension,
	ExtensionContextsMapNew,
} from '@jira/forge__forge-ui-types/src/common/types/extension.tsx';
import type { ExtensionPointModule } from '@jira/forge__forge-ui-types/src/common/types/module.tsx';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { canFetchForgeModules } from '../can-fetch-forge/index.tsx';
import QUERY, {
	type doFetchWithGraphQLErrorsTrackingQuery$data,
} from './__generated__/doFetchWithGraphQLErrorsTrackingQuery.graphql';
import { toMappedExtensions } from './data-transformer/index.tsx';
import { trackGraphQLErrors } from './errors/trackGraphQLErrors.tsx';

type CloudId = string;

type FetchParams<ModuleType> = {
	cloudId: CloudId;
	isAnonymous: boolean;
	types: ModuleType[];
	context: { issueKey?: string; projectKey?: string };
	includeHidden: boolean;
	includeScopes?: boolean;
	source?: Source;
	preferCache?: boolean;
	ufoFetchExperienceKey?: string;
};

export const fetchModules = async <
	ModuleType extends ExtensionPointModule,
	Response = {
		[Key in ModuleType]: ExtensionContextsMapNew[Key][];
	},
>(
	params: FetchParams<ModuleType>,
): Promise<Response> => {
	const { types, cloudId, source = SOURCE_UNKNOWN } = params;

	if (!canFetchForgeModules({ cloudId })) {
		return toMappedExtensions(types, []);
	}

	try {
		const extensions = await measureExecutionTimeMetrics(
			() => doFetchWithGraphQLErrorsTracking(params),
			{
				ufoExperienceKey: params.ufoFetchExperienceKey,
			},
		);

		if (Math.random() * 100 < FORGE_SAMPLING_RATE) {
			fireInitializationFetchFinishedEvent(source);
		}

		return toMappedExtensions(types, extensions);
	} catch (error: unknown) {
		const errorType = getErrorType(error);

		fireInitializationFetchFailedEvent(source, {
			isSSR: __SERVER__,
			errorType,
			error,
		});

		if (EXCLUDED_ERROR_TYPES.includes(errorType)) {
			return toMappedExtensions(types, []);
		}

		throw error;
	}
};

export function doFetchWithGraphQLErrorsTracking<ModuleType extends ExtensionPointModule>(
	props: FetchParams<ModuleType>,
): Promise<AggExtension[]> {
	// includeScopes is true by default to enable decoupling scopes from forge app
	const {
		cloudId,
		types,
		context,
		includeHidden,
		includeScopes = true,
		preferCache = false,
	} = props;

	if (types.length === 0) {
		throw new Error('No modules to fetch provided');
	}

	const operationName = QUERY.params.name;
	const { stopGraphQLErrorTracking, getGraphQLError } = trackGraphQLErrors(operationName);
	let extensions: AggExtension[] | null;

	return new Promise((resolve, reject) => {
		const subscription = fetchQuery(
			getRelayEnvironment(),
			/* eslint-disable @atlassian/relay/unused-fields */
			graphql`
				query doFetchWithGraphQLErrorsTrackingQuery(
					$cloudId: ID!
					$context: JiraExtensionRenderingContextInput!
					$types: [String!]!
					$includeHidden: Boolean!
					$includeScopes: Boolean!
				) {
					jira {
						forge {
							extensions(
								cloudId: $cloudId
								context: $context
								types: $types
								includeHidden: $includeHidden
							) {
								hiddenBy @include(if: $includeHidden)
								scopes @include(if: $includeScopes)
								type
								id
								moduleId
								environmentId
								environmentKey
								environmentType
								installationId
								remoteInstallationRegion
								appVersion
								consentUrl
								properties
								userAccess {
									hasAccess
									enabled
								}
								egress {
									addresses
									type
									category
									inScopeEUD
								}
								sandboxPolicies
								installationConfig {
									key
									value
								}
								license {
									active
									type
									supportEntitlementNumber
									trialEndDate
									subscriptionEndDate
									isEvaluation
									billingPeriod
									ccpEntitlementId
									ccpEntitlementSlug
									capabilitySet
								}
							}
						}
					}
				}
			`,
			/* eslint-enable @atlassian/relay/unused-fields */
			{
				cloudId,
				context,
				types,
				includeHidden,
				includeScopes,
			},
			preferCache
				? {
						fetchPolicy: 'store-or-network',
					}
				: {},
		).subscribe({
			next: (response) => {
				stopGraphQLErrorTracking();
				// oxlint-disable-next-line typescript/consistent-type-assertions
				const responseExtensions = (response as doFetchWithGraphQLErrorsTrackingQuery$data).jira
					?.forge?.extensions;
				extensions =
					responseExtensions?.concat().map((ext) => ({
						...ext,
						moduleId: ext.moduleId ?? null,
					})) || null;
			},
			error: (error: unknown) => {
				stopGraphQLErrorTracking();
				reject(error);
				subscription.unsubscribe();
			},
			complete: () => {
				// If there is a GraphQL error and extensions are `null`, it means a critical error has happened, and we have to reject the promise.
				// However, if the list of extensions is present in the response, it shouldn't be considered an error.
				const graphQLError = getGraphQLError();
				if (graphQLError) {
					extensions != null ? resolve(extensions || []) : reject(graphQLError);
				} else {
					resolve(extensions || []);
				}
				subscription.unsubscribe();
			},
		});
	});
}

export const EXCLUDED_ERROR_TYPES: string[] = [
	FAILED_TO_FETCH,
	AUTHORIZATION_FAILED_INSUFFICIENT_PERMISSIONS,
	DATA_CLASSIFICATION_NO_ACCESS,
];
